import { ActionIcon, Button, Group, Stack, Table, Text, TextInput, Tooltip } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { ProductSelect } from './ProductSelect';
import { LocationSelect } from './LocationSelect';

/** One line the user is editing. `key` is a client-only React key (never sent to the
 * API — the server assigns `lineNo` on create/update); `productLabel`/`locationLabel`
 * are denormalized alongside their ids purely so the review step's ledger-effect
 * preview can render names without a second round of fetches. */
export interface LineFormRow {
  key: string;
  productId: string;
  productLabel: string;
  /** Only meaningful when the editor is rendered with `includeLocation` (stores
   * issues — each line names its own from-location). Transfers fix from/to location
   * once at the header level, so this stays empty for transfer lines. */
  locationId: string;
  locationLabel: string;
  qty: string;
}

export function emptyLineRow(): LineFormRow {
  return { key: crypto.randomUUID(), productId: '', productLabel: '', locationId: '', locationLabel: '', qty: '' };
}

interface LineRowsEditorProps {
  rows: LineFormRow[];
  onChange: (rows: LineFormRow[]) => void;
  /** Required for the from-location column and for scoping `LocationSelect`'s
   * lookup to the right site. */
  siteId: string | undefined;
  includeLocation: boolean;
  locationLabel?: string;
}

/** Repeatable product/qty (+ optional location) line rows shared by the stores-issue
 * and transfer guided-create flows (BUILD_SPEC.md §8.6: "add lines"). */
export function LineRowsEditor({ rows, onChange, siteId, includeLocation, locationLabel = 'From location' }: LineRowsEditorProps) {
  function updateRow(key: string, patch: Partial<LineFormRow>) {
    onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  function removeRow(key: string) {
    onChange(rows.filter((row) => row.key !== key));
  }

  function addRow() {
    onChange([...rows, emptyLineRow()]);
  }

  return (
    <Stack gap="xs">
      <Table.ScrollContainer minWidth={640}>
        <Table verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Product</Table.Th>
              {includeLocation && <Table.Th>{locationLabel}</Table.Th>}
              <Table.Th w={140}>
                {/* Unit depends on the chosen product's own base UOM (kg/each/L — BASE_UOMS in
                    packages/core/src/enums.ts), so the header can't hardcode one; a tooltip fills
                    the gap instead of a `description` on each repeated row input, which would grow
                    the table row height once per line. */}
                <Tooltip label="Quantity in the product's own unit — kg allows decimals, each is a whole number" openDelay={300}>
                  <span>Qty</span>
                </Tooltip>
              </Table.Th>
              <Table.Th w={48} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr key={row.key}>
                <Table.Td>
                  <ProductSelect
                    value={row.productId}
                    onChange={(productId, productLabel) => updateRow(row.key, { productId, productLabel })}
                    aria-label="Product"
                  />
                </Table.Td>
                {includeLocation && (
                  <Table.Td>
                    <LocationSelect
                      siteId={siteId}
                      value={row.locationId}
                      onChange={(locationId, locLabel) => updateRow(row.key, { locationId, locationLabel: locLabel })}
                      aria-label={locationLabel}
                    />
                  </Table.Td>
                )}
                <Table.Td>
                  <TextInput
                    aria-label="Quantity"
                    placeholder="0.000"
                    value={row.qty}
                    onChange={(event) => updateRow(row.key, { qty: event.currentTarget.value })}
                  />
                </Table.Td>
                <Table.Td>
                  <Tooltip label="Removes this line from the document — the other lines are unaffected" openDelay={300}>
                    <span>
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        aria-label="Remove line"
                        onClick={() => removeRow(row.key)}
                        disabled={rows.length <= 1}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </span>
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Group justify="space-between">
        <Button variant="light" leftSection={<IconPlus size={16} />} onClick={addRow}>
          Add line
        </Button>
        {rows.length === 0 && (
          <Text size="sm" c="dimmed">
            At least one line is required.
          </Text>
        )}
      </Group>
    </Stack>
  );
}
